import { useQuery, useQueryClient } from 'react-query';
import { getJobs } from 'api/services/jobs';
import { auth, useAuthState } from 'api/services/firebase';
import { getSampleJob } from 'hooks/useRunABJob';
import { getSavedAccountMode } from 'utils/helpers';
import { shortDateFormatter } from 'utils/formatDateTime';
import { roles } from 'constants/permissions';
import useAccountStore from '../store/accountStore';

const useGetJobs = (page, count, filter) => {
  const queryClient = useQueryClient();
  const [user] = useAuthState(auth);
  const activeMode = getSavedAccountMode(user?.uid) || roles.user;
  const isUserAvailable = !!user;
  const teamId = useAccountStore((state) => state?.activeAccount?.teamId);

  if (!isUserAvailable) {
    queryClient.removeQueries({ queryKey: ['jobs', page, count, teamId, filter] });
  }

  const {
    data = null,
    error,
    refetch,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: ['jobs', page, count, teamId, filter],
    queryFn: getJobs(page, count, teamId, filter),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    enabled: isUserAvailable,
  });

  if (!isLoading && data !== null) {
    const sampleJob = activeMode === roles.user ? getSampleJob() : null;
    const { jobs: pageData, total } = data;

    let allJobs = pageData ? [...pageData] : [];

    if (allJobs.length < count) { // not a full page means it is the last page
      allJobs = sampleJob ? [...allJobs, sampleJob] : allJobs;
    }

    const jobs = allJobs?.map((jobEntry) => ({
      ...jobEntry,
      device: (jobEntry.device || '').toUpperCase(),
      createdOn: shortDateFormatter(jobEntry.createdOn),
      runEnd: jobEntry.runEnd ? shortDateFormatter(jobEntry.runEnd) : '-',
      cost: `$${jobEntry.cost / 100}`,
    }));

    return {
      jobs,
      total: sampleJob ? total + 1 : total,
      error,
      refetch,
      isLoading,
      isRefetching,
    };
  }

  if (!isUserAvailable) {
    const guestSampleJob = getSampleJob() || null;
    if (guestSampleJob) {
      guestSampleJob.createdOn = shortDateFormatter(guestSampleJob?.createdOn);
    }
    return {
      jobs: guestSampleJob ? [guestSampleJob] : [],
      total: guestSampleJob ? 1 : 0,
      error,
      refetch,
      isLoading,
      isRefetching,
    };
  }

  return {
    jobs: [],
    total: 0,
    error,
    refetch,
    isLoading,
    isRefetching,
  };
};

export default useGetJobs;
